import { useMutation } from "@apollo/client";
import { Button, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";

import { BULK_UPDATE_PIPELINE_OUTPUT_DATA_MUTATION } from "./api";

type ColumnOption = { value: string; label: string };

type BulkEditColumnRow = {
  id: string;
  currentValue?: string | null;
  tags?: string[] | null;
  reportable?: string | null;
  overrideManualEdits?: string | null;
};

type BulkEditColumnDialogProps = {
  open: boolean;
  onClose: () => void;
  fieldName: string;
  fieldLabel: string;
  options: ColumnOption[];
  selectedRows: BulkEditColumnRow[];
};

export function BulkEditColumnDialog({
  open,
  onClose,
  fieldName,
  fieldLabel,
  options,
  selectedRows,
}: BulkEditColumnDialogProps) {
  const alerts = useAlerts();
  const [bulkUpdate, { loading }] = useMutation(BULK_UPDATE_PIPELINE_OUTPUT_DATA_MUTATION);

  // Pre-select the current value if all rows share the same value
  const sharedValue = useMemo(() => {
    if (selectedRows.length === 0) return "";
    const first = selectedRows[0].currentValue;
    const allSame = selectedRows.every((r) => r.currentValue === first);
    return allSame && first ? first : "";
  }, [selectedRows]);

  const [selectedValue, setSelectedValue] = useState(sharedValue);

  const handleSubmit = useCallback(async () => {
    if (!selectedValue) return;

    const count = selectedRows.length;
    const rowMap = new Map(selectedRows.map((r) => [r.id, r]));

    onClose();

    try {
      await bulkUpdate({
        variables: {
          input: {
            updates: selectedRows.map((r) => ({
              id: r.id,
              fields: { [fieldName]: selectedValue },
            })),
          },
        },
        optimisticResponse: {
          bulkUpdatePipelineOutputData: selectedRows.map((r) => {
            const row = rowMap.get(r.id);
            return {
              __typename: "WasteManifestReviewRow" as const,
              id: r.id,
              tags: row?.tags ?? null,
              reportable: fieldName === "reportable" ? selectedValue : (row?.reportable ?? null),
              overrideManualEdits:
                fieldName === "overrideManualEdits"
                  ? selectedValue
                  : (row?.overrideManualEdits ?? null),
            };
          }),
        },
      });

      const label = options.find((o) => o.value === selectedValue)?.label ?? selectedValue;
      alerts.success(`Set ${fieldLabel} to "${label}" for ${count} ${pluralize("row", count)}`);
    } catch (err) {
      alerts.error(`Failed to update ${fieldLabel}`, err);
    }
  }, [bulkUpdate, alerts, onClose, selectedRows, fieldName, fieldLabel, selectedValue, options]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={`Set ${fieldLabel}`}
      description={`Update ${selectedRows.length} selected ${pluralize("row", selectedRows.length)}.`}
      primaryActionButton={
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedValue || loading}>
          Apply
        </Button>
      }
    >
      <FormControl fullWidth size="small">
        <InputLabel>{fieldLabel}</InputLabel>
        <Select
          value={selectedValue}
          onChange={(e) => setSelectedValue(e.target.value)}
          label={fieldLabel}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Dialog>
  );
}
